/* eslint-disable max-lines */
import { reprocessEnvelope } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import Permissions from 'components/Permissions';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCommunicationsLlmChain } from 'selectors/communications';
import {
  getEnvelope,
  getEnvelopeEsDocs,
  getEnvelopeEsDocsAsIndexed,
  getReprocessEnvelopeLoading,
} from 'selectors/envelopes';
import { getEventsByEnvelopeId } from 'selectors/events';
import { useSelector } from 'store';
import JsonModal from '../../JsonModal';
import EnvelopeEsDocs from '../EnvelopeEsDocs';

type EnvelopeJsonType = 'comm' | 'esDocs' | 'esDocsAsIndexed' | 'event' | 'chain';

const EnvelopeSidebarAdvanced: React.FC = () => {
  const dispatch = useDispatch();

  const { envelopeId } = useParams<{ envelopeId: string }>();
  const events = useSelector(getEventsByEnvelopeId(envelopeId));
  const reprocessLoading = useSelector(getReprocessEnvelopeLoading);
  const envelope = useSelector((state) => getEnvelope(state, envelopeId));
  const envelopeEsDocs = useSelector(getEnvelopeEsDocs);
  const envelopeEsDocsAsIndexed = useSelector(getEnvelopeEsDocsAsIndexed);

  const llmChains = useSelector(getCommunicationsLlmChain);
  const [jsonModal, setJsonModal] = useState<EnvelopeJsonType | false>(false);
  const [reprocessType, setReprocessType] = useState<'env' | 'envStarship' | 'envNewDoc'>();

  const toggleJsonModal = (type: EnvelopeJsonType | false): void => {
    setJsonModal(type);
  };

  const reprocessStarship = (): void => {
    setReprocessType('envStarship');
    dispatch(reprocessEnvelope({ envelopeId, starship: true }));
  };

  const reprocessNewDoc = (): void => {
    setReprocessType('envNewDoc');
    dispatch(reprocessEnvelope({ envelopeId, starship: true, regenerate: true }));
  };

  return (
    <div className="p-4 bg-white border border-litlingo-gray-1">
      <Permissions action="communications.viewCommunicationsAsJson">
        <div>
          <button
            type="button"
            className="text-litlingo-primary font-medium focus:outline-none"
            onClick={(): void => toggleJsonModal('comm')}
          >
            View Communication as Json
          </button>
        </div>
      </Permissions>
      <Permissions action="communications.viewCommunicationsAsJson">
        <EnvelopeEsDocs toggleJsonModal={toggleJsonModal} />
      </Permissions>
      <Permissions action="communications.reprocess">
        <div className="flex flex-row items-center">
          <button
            type="button"
            className="text-litlingo-primary font-medium focus:outline-none mr-2"
            onClick={(): void => reprocessStarship()}
          >
            Reprocess Starship
          </button>
          {reprocessLoading && reprocessType === 'envStarship' && <LoadingIndicator size="4" />}
        </div>
      </Permissions>
      <Permissions action="communications.reprocess">
        <div className="flex flex-row items-center">
          <button
            type="button"
            className="text-litlingo-primary font-medium focus:outline-none mr-2"
            onClick={(): void => reprocessNewDoc()}
          >
            Reprocess New Document
          </button>
          {reprocessLoading && reprocessType === 'envStarship' && <LoadingIndicator size="4" />}
        </div>
      </Permissions>

      <Permissions action="communications.viewCommunicationsAsJson">
        <div className="flex flex-row items-center">
          <button
            type="button"
            data-testid="view-as-json-button"
            className="text-litlingo-primary font-medium focus:outline-none"
            onClick={(): void => toggleJsonModal('event')}
          >
            View Events as Json
          </button>
        </div>
      </Permissions>

      <Permissions action="communications.viewCommunicationsAsJson">
        <div className="flex flex-row items-center">
          <button
            type="button"
            data-testid="view-as-json-button"
            className="text-litlingo-primary font-medium focus:outline-none"
            onClick={(): void => toggleJsonModal('chain')}
          >
            View LLM chain
          </button>
        </div>
      </Permissions>
      {jsonModal === 'comm' && envelope.communications && (
        <JsonModal
          title="Communications"
          data={envelope.communications}
          toggleShowModal={(): void => toggleJsonModal(false)}
          name="communication"
        />
      )}
      {jsonModal === 'esDocs' && envelopeEsDocs && (
        <JsonModal
          title="Es Docs"
          data={envelopeEsDocs}
          toggleShowModal={(): void => toggleJsonModal(false)}
          name="esDocs"
        />
      )}
      {jsonModal === 'esDocsAsIndexed' && envelopeEsDocsAsIndexed && (
        <JsonModal
          title="Es Docs As Indexed"
          data={envelopeEsDocsAsIndexed}
          toggleShowModal={(): void => toggleJsonModal(false)}
          name="esDocsAsIndexed"
        />
      )}
      {jsonModal === 'event' && events && (
        <JsonModal
          title="Events"
          data={events}
          toggleShowModal={(): void => toggleJsonModal(false)}
          name="events"
        />
      )}
      {jsonModal === 'chain' && events && (
        <JsonModal
          title="Events"
          data={llmChains}
          toggleShowModal={(): void => toggleJsonModal(false)}
          name="Llm chains"
        />
      )}
    </div>
  );
};

export default EnvelopeSidebarAdvanced;
